"use client";

import { ClerkProvider } from "@clerk/nextjs";
import { enUS, esES } from "@clerk/localizations";
import { useEffect, useState, type ComponentProps } from "react";
import {
  type SupportedLocale,
  resolveSupportedLocale,
} from "@/lib/i18n/config";
import i18n, { initI18n } from "@/lib/infra/i18n";

type ClerkLocalization = NonNullable<
  ComponentProps<typeof ClerkProvider>["localization"]
>;

const toClerkLocalization = (value: unknown): ClerkLocalization =>
  value as ClerkLocalization;

const FALLBACK_LOCALE = toClerkLocalization(enUS);
const ES_LOCALE = toClerkLocalization(esES);
const SUPPORTED_LOCALES: Record<SupportedLocale, ClerkLocalization> = {
  en: FALLBACK_LOCALE,
  es: ES_LOCALE,
};

function resolveLocalization(language?: string): ClerkLocalization {
  const code = resolveSupportedLocale(language);
  return SUPPORTED_LOCALES[code] ?? FALLBACK_LOCALE;
}

type ClerkLocalizationProviderProps = {
  children: React.ReactNode;
  initialLocale: SupportedLocale;
};

export default function ClerkLocalizationProvider({
  children,
  initialLocale,
}: ClerkLocalizationProviderProps) {
  initI18n(initialLocale);

  const [localization, setLocalization] = useState<ClerkLocalization>(() =>
    resolveLocalization(initialLocale)
  );

  useEffect(() => {
    const handleChange = (lng: string) => {
      setLocalization(resolveLocalization(lng));
    };

    handleChange(initialLocale);
    i18n.on("languageChanged", handleChange);

    return () => {
      i18n.off("languageChanged", handleChange);
    };
  }, [initialLocale]);

  return (
    <ClerkProvider
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignUpUrl="/home?onboarding=1"
      afterSignInUrl="/home"
      localization={localization}
    >
      {children}
    </ClerkProvider>
  );
}
